import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { formatCurrency } from '../../CoreFunctions';
import { useNavigate } from 'react-router-dom';

// A sleek, modern pictorial balance sheet: Assets (left), Liabilities (bottom-right), Equity stacked above Liabilities
// Totals are aggregated client-side from existing endpoints until a dedicated aggregate API is provided.
export default function BalancePictorial() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    assets: { physical: 0, invested: 0, cash: 0 },
    liabilities: { loans: 0, revolvers: 0, mortgages: 0, auto_loans: 0, four01k_loans: 0 },
  });
  // Baseline snapshot for delta computations (computed per selected period)
  const [baselineData, setBaselineData] = useState({
    assets: { physical: 0, invested: 0, cash: 0 },
    liabilities: { loans: 0, revolvers: 0, mortgages: 0, auto_loans: 0, four01k_loans: 0 },
  });
  const [baselineMonthUsed, setBaselineMonthUsed] = useState(''); // YYYY-MM used as baseline for deltas
  // Month snapshot selector (YYYY-MM)
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  // Compute last completed month (disallow current month and future months)
  const getLastCompletedMonth = (refDate = new Date()) => {
    const y = refDate.getFullYear();
    const m = refDate.getMonth(); // 0-based current month
    // last completed month is previous calendar month
    const d = new Date(y, m - 1, 1);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  };
  const lastCompletedMonth = getLastCompletedMonth(now);
  const [month, setMonth] = useState(lastCompletedMonth);
  // Period presets for deltas and change toggle
  // Allowed: '1m' | '3m' | '6m' | '12m' | 'ytd' | 'all'
  const [period, setPeriod] = useState('ytd');
  // Change display mode: '$' or '%'
  const [changeMode, setChangeMode] = useState('$');

  // Robust numeric coercion: supports djmoney objects, strings, nulls
  const num = (val) => {
    try {
      if (val == null) return 0;
      if (typeof val === 'number') return Number.isFinite(val) ? val : 0;
      if (typeof val === 'string') {
        const n = Number(val);
        return Number.isFinite(n) ? n : 0;
      }
      // djmoney Money or similar {amount: "123.45", currency: "USD"}
      if (typeof val === 'object') {
        if (Object.prototype.hasOwnProperty.call(val, 'amount')) {
          const n = Number(val.amount);
          return Number.isFinite(n) ? n : 0;
        }
        const maybe = Number(val.valueOf ? val.valueOf() : val);
        return Number.isFinite(maybe) ? maybe : 0;
      }
    } catch (e) {
      // ignore
    }
    return 0;
  };

  // Date helpers + interpolation for physical monthly
  const parseDate = (s) => {
    const parts = String(s).split('-');
    const y = Number(parts[0]);
    const m = Number(parts[1] || 1) - 1;
    const d = Number(parts[2] || 1);
    return new Date(y, m, d);
  };
  const ym = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  const lastDayOfMonth = (y, mIndex) => new Date(y, mIndex + 1, 0).getDate();
  const toMonthlyInterpolatedMap = (sparsePoints, monthLabels) => {
    const pts = (sparsePoints || [])
      .map(p => ({ t: parseDate(p.date), v: num(p.value) }))
      .filter(p => !isNaN(p.t.getTime()))
      .sort((a,b)=>a.t-b.t);
    const byMonth = {};
    for (const p of pts) byMonth[ym(p.t)] = p.v;
    if (pts.length === 0) {
      const out = {}; for (const m of monthLabels) out[m] = 0; return out;
    }
    for (let i=0;i<pts.length-1;i++){
      const a = pts[i], b = pts[i+1];
      let cur = new Date(a.t.getFullYear(), a.t.getMonth()+1, 1);
      while (cur <= new Date(b.t.getFullYear(), b.t.getMonth(), 1)){
        const k = ym(cur);
        if (byMonth[k] == null){
          const totalDays = (b.t - a.t) / (1000*60*60*24);
          const curEnd = new Date(cur.getFullYear(), cur.getMonth(), lastDayOfMonth(cur.getFullYear(), cur.getMonth()));
          const daysFromA = (curEnd - a.t) / (1000*60*60*24);
          const ratio = Math.max(0, Math.min(1, daysFromA / totalDays));
          byMonth[k] = a.v + (b.v - a.v) * ratio;
        }
        cur.setMonth(cur.getMonth()+1);
      }
    }
    const last = pts[pts.length-1];
    const start = parseDate(`${monthLabels[0]}-01`);
    const end = parseDate(`${monthLabels[monthLabels.length-1]}-01`);
    let cur = new Date(last.t.getFullYear(), last.t.getMonth(), 1);
    while (cur <= end){
      const k = ym(cur);
      if (byMonth[k] == null) byMonth[k] = last.v;
      cur.setMonth(cur.getMonth()+1);
    }
    const out = {}; for (const m of monthLabels) out[m] = byMonth[m] != null ? byMonth[m] : 0; return out;
  };

  // Helper: subtract N months from YYYY-MM
  const subtractMonths = (ymStr, n) => {
    try {
      const [yy, mm] = ymStr.split('-').map(Number);
      const d = new Date(yy, mm - 1, 1);
      d.setMonth(d.getMonth() - n);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    } catch {
      return ymStr;
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      try {
        // Build start/end dates for selected month
        const [yy, mm] = month.split('-').map(Number);
        const start = `${yy}-${pad(mm)}-01`;
        const end = `${yy}-${pad(mm)}-${pad(lastDayOfMonth(yy, mm-1))}`;

        // Fetch histories for snapshot/baseline computations
        const [physHist, invHist, cashHist] = await Promise.all([
          axios.get('/api/balance_sheet/assets/physical_assets/market_values/history/'),
          axios.get('/api/invested_assets/accounts/values/history/'),
          axios.get('/api/balance_sheet/assets/cash_accounts/balances/history/'),
        ]);

        // Determine baseline month based on selected preset
        const computeEarliestMonth = () => {
          const allDates = [];
          const pushDates = (arr) => {
            for (const it of (arr || [])) for (const p of (it.series || [])) allDates.push(p.date);
          };
          pushDates(physHist.data);
          pushDates(invHist.data);
          pushDates(cashHist.data);
          if (allDates.length === 0) return month; // fallback
          const earliest = allDates.map(parseDate).sort((a,b)=>a-b)[0];
          return `${earliest.getFullYear()}-${pad(earliest.getMonth()+1)}`;
        };
        let baselineMonth = month;
        switch (period) {
          case '1m': baselineMonth = subtractMonths(month, 1); break;
          case '3m': baselineMonth = subtractMonths(month, 3); break;
          case '6m': baselineMonth = subtractMonths(month, 6); break;
          case '12m': baselineMonth = subtractMonths(month, 12); break;
          case 'ytd': {
            const [y] = month.split('-').map(Number);
            baselineMonth = `${y-1}-12`;
            break;
          }
          case 'all': baselineMonth = computeEarliestMonth(); break;
          default: baselineMonth = 'ytd';
        }
        // Ensure baseline is not after snapshot
        if (baselineMonth > month) baselineMonth = month;

        // Physical: sum interpolated value for selected month (exclude structures/land)
        const physicalMonthly = toMonthlyInterpolatedMap(
          (() => {
            const map = new Map();
            for (const it of (physHist.data || [])) {
              if (it.asset_type_2 === 'structure' || it.asset_type_2 === 'land') continue;
              for (const p of (it.series || [])) {
                const k = p.date; const cur = map.get(k) || 0; map.set(k, cur + num(p.value));
              }
            }
            return Array.from(map.entries()).map(([date, value]) => ({ date, value }));
          })(),
          [month, baselineMonth]
        );
        const physical = physicalMonthly[month] || 0;
        const physicalBase = physicalMonthly[baselineMonth] || 0;

        // Helpers for account openness and balances (reuse from prior)
        const isOpenAccount = (a) => {
          try {
            if (a == null) return false;
            if (typeof a.is_open === 'boolean') return a.is_open;
            if (typeof a.isActive === 'boolean') return a.isActive;
            if (a.closed === true) return false;
            if (a.closed_date != null && a.closed_date !== '') return false;
            if (a.close_date != null && a.close_date !== '') return false;
            if (a.status) {
              const s = String(a.status).toLowerCase();
              if (s.includes('closed') || s.includes('inactive')) return false;
              if (s.includes('open') || s.includes('active')) return true;
            }
          } catch (e) { /* ignore */ }
          // Default to open when unknown to avoid hiding active accounts inadvertently
          return true;
        };

        const getCashBalance = (a) => {
          // Prefer common field names, fallback to nested
          if (a == null) return 0;
          if (a.current_balance !== undefined) return num(a.current_balance);
          if (a.balance !== undefined) return num(a.balance);
          if (a.latest_balance !== undefined) return num(a.latest_balance);
          if (a.balance_amount !== undefined) return num(a.balance_amount);
          if (a?.last_balance?.amount !== undefined) return num(a.last_balance.amount);
          return 0;
        };

        // Invested: prefer constructed positions for snapshot and baseline months; fallback to accounts values history
        const computeInvestedFromPositions = async (targetMonth) => {
          const [y2, m2] = targetMonth.split('-').map(Number);
          const s = `${y2}-${pad(m2)}-01`;
          const e = `${y2}-${pad(m2)}-${pad(lastDayOfMonth(y2, m2-1))}`;
          const params = new URLSearchParams({ start: s, end: e, excludeZero: '1' });
          const posResp = await axios.get(`/api/invested_assets/positions/by_day/?${params.toString()}`);
          const byDate = (posResp.data && posResp.data.byDate) || {};
          // Aggregate per-day per-account total MV
          const dayAcctMv = {};
          for (const [dStr, rows] of Object.entries(byDate)) {
            const acctMap = {};
            for (const r of rows || []) {
              const acct = r.account || 'unknown';
              const mv = Number(r.mv) || 0;
              acctMap[acct] = (acctMap[acct] || 0) + mv;
            }
            dayAcctMv[dStr] = acctMap;
          }
          const monthAcctLatest = {};
          for (const [dStr, acctMap] of Object.entries(dayAcctMv)) {
            const m = dStr.slice(0, 7);
            if (m !== targetMonth) continue;
            for (const [acct, mv] of Object.entries(acctMap)) {
              const cur = monthAcctLatest[acct] || { date: '0000-00-00', mv: 0 };
              if (dStr >= cur.date) monthAcctLatest[acct] = { date: dStr, mv: Number(mv) || 0 };
            }
          }
          const sum = Object.values(monthAcctLatest).reduce((s, v) => s + (Number(v.mv) || 0), 0);
          const hasAny = Object.keys(byDate).length > 0;
          return { sum, hasAny };
        };

        // Snapshot invested
        let invested = 0;
        try {
          const { sum, hasAny } = await computeInvestedFromPositions(month);
          invested = sum;
          if (!hasAny || invested === 0) {
            // Fallback to invested accounts values history
            let total = 0;
            for (const it of (invHist.data || [])) {
              let bestVal = null; let bestDate = '';
              for (const p of (it.series || [])) {
                const k = String(p.date).slice(0,7);
                if (k === month) { const d = String(p.date); if (!bestDate || d > bestDate) { bestDate = d; bestVal = num(p.value); } }
              }
              if (bestVal != null) total += bestVal;
            }
            invested = total;
          }
        } catch (e) {
          // Fallback to history on error
          let total = 0;
          for (const it of (invHist.data || [])) {
            let bestVal = null; let bestDate = '';
            for (const p of (it.series || [])) {
              const k = String(p.date).slice(0,7);
              if (k === month) { const d = String(p.date); if (!bestDate || d > bestDate) { bestDate = d; bestVal = num(p.value); } }
            }
            if (bestVal != null) total += bestVal;
          }
          invested = total;
        }

        // Baseline invested
        let investedBase = 0;
        try {
          const { sum, hasAny } = await computeInvestedFromPositions(baselineMonth);
          investedBase = sum;
          if (!hasAny || investedBase === 0) {
            let total = 0;
            for (const it of (invHist.data || [])) {
              let bestVal = null; let bestDate = '';
              for (const p of (it.series || [])) {
                const k = String(p.date).slice(0,7);
                if (k === baselineMonth) { const d = String(p.date); if (!bestDate || d > bestDate) { bestDate = d; bestVal = num(p.value); } }
              }
              if (bestVal != null) total += bestVal;
            }
            investedBase = total;
          }
        } catch (e) {
          let total = 0;
          for (const it of (invHist.data || [])) {
            let bestVal = null; let bestDate = '';
            for (const p of (it.series || [])) {
              const k = String(p.date).slice(0,7);
              if (k === baselineMonth) { const d = String(p.date); if (!bestDate || d > bestDate) { bestDate = d; bestVal = num(p.value); } }
            }
            if (bestVal != null) total += bestVal;
          }
          investedBase = total;
        }

        // Cash: pick monthly statement balances per account and sum for snapshot and baseline months
        const cash = (() => {
          let sum = 0;
          for (const it of (cashHist.data || [])) {
            // Only include open accounts if status present (we don't have flags here normally, so include all)
            let bestVal = null; let bestDate = '';
            for (const p of (it.series || [])) {
              const k = String(p.date).slice(0,7);
              if (k === month) {
                const d = String(p.date);
                if (!bestDate || d > bestDate) { bestDate = d; bestVal = num(p.value); }
              }
            }
            if (bestVal != null) sum += bestVal;
          }
          return sum;
        })();
        const cashBase = (() => {
          let sum = 0;
          for (const it of (cashHist.data || [])) {
            let bestVal = null; let bestDate = '';
            for (const p of (it.series || [])) {
              const k = String(p.date).slice(0,7);
              if (k === baselineMonth) {
                const d = String(p.date);
                if (!bestDate || d > bestDate) { bestDate = d; bestVal = num(p.value); }
              }
            }
            if (bestVal != null) sum += bestVal;
          }
          return sum;
        })();

        // Liabilities: use analytics for baseline..snapshot range (group_by=month)
        const finParams = { group_by: 'month', start_date: `${baselineMonth}-01`, end_date: `${yy}-${pad(mm)}-01` };
        const finAnal = await axios.get('/api/balance_sheet/financing/analytics/', { params: finParams });
        const byType = (finAnal.data && finAnal.data.by_type) || {};
        const periods = (finAnal.data && finAnal.data.periods) || [];
        const idxSnap = periods.indexOf(month);
        const idxBase = periods.indexOf(baselineMonth);
        let mortgages = 0, auto_loans = 0, four01k_loans = 0, revolving = 0, loans = 0;
        let mortgagesBase = 0, auto_loansBase = 0, four01k_loansBase = 0, revolvingBase = 0, loansBase = 0;
        const pickAt = (key, idx) => Number(((byType[key]?.outstanding)||[])[idx]) || 0;
        if (idxSnap >= 0) {
          const cc = pickAt('credit_cards', idxSnap);
          const heloc = pickAt('helocs', idxSnap);
          const rev = pickAt('revolvers', idxSnap);
          mortgages = pickAt('mortgages', idxSnap);
          auto_loans = pickAt('auto_loans', idxSnap);
          four01k_loans = pickAt('four01k_loans', idxSnap);
          revolving = cc + heloc + rev;
          loans = mortgages + auto_loans + four01k_loans + revolving;
        }
        if (idxBase >= 0) {
          const ccB = pickAt('credit_cards', idxBase);
          const helocB = pickAt('helocs', idxBase);
          const revB = pickAt('revolvers', idxBase);
          mortgagesBase = pickAt('mortgages', idxBase);
          auto_loansBase = pickAt('auto_loans', idxBase);
          four01k_loansBase = pickAt('four01k_loans', idxBase);
          revolvingBase = ccB + helocB + revB;
          loansBase = mortgagesBase + auto_loansBase + four01k_loansBase + revolvingBase;
        }

        if (!cancelled) {
          setData({ assets: { physical, invested, cash }, liabilities: { loans, revolvers: revolving, mortgages, auto_loans, four01k_loans } });
          setBaselineData({ assets: { physical: physicalBase, invested: investedBase, cash: cashBase }, liabilities: { loans: loansBase, revolvers: revolvingBase, mortgages: mortgagesBase, auto_loans: auto_loansBase, four01k_loans: four01k_loansBase } });
          setBaselineMonthUsed(baselineMonth);
        }
      } catch (e) {
        console.error('Failed to load balance sheet aggregates', e);
        if (!cancelled) setError('Failed to load balance data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [month, period]);

  const totals = useMemo(() => {
    const totalAssets = (data.assets.physical || 0) + (data.assets.invested || 0) + (data.assets.cash || 0);
    // Liabilities total should equal the sum of visible parts.
    // We already computed data.liabilities.loans as (mortgages + auto_loans + revolvers),
    // so do NOT add revolvers again here.
    const totalLiab = (data.liabilities.loans || 0);
    const equity = totalAssets - totalLiab;
    const baseTotalAssets = (baselineData.assets.physical || 0) + (baselineData.assets.invested || 0) + (baselineData.assets.cash || 0);
    const baseTotalLiab = (baselineData.liabilities.loans || 0);
    const baseEquity = baseTotalAssets - baseTotalLiab;
    return { totalAssets, totalLiab, equity, baseTotalAssets, baseTotalLiab, baseEquity };
  }, [data, baselineData]);

  // Visual scale
  const maxHeightPx = 420; // canvas height for the tallest column
  const assetsHeight = totals.totalAssets <= 0 ? 0 : maxHeightPx; // Assets define the reference height when positive
  const scale = totals.totalAssets > 0 ? (assetsHeight / totals.totalAssets) : 1;
  const liabHeightRaw = totals.totalLiab * scale;
  const equityHeightRaw = Math.max(0, totals.equity) * scale; // clamp to >= 0 for stack

  // If liabilities exceed assets, ensure container tall enough to show overflow gracefully
  // Note: we avoid extra margins between Liabilities and Equity so that combined heights match Assets exactly.
  const containerHeight = Math.max(assetsHeight, liabHeightRaw + equityHeightRaw, 200);

  const pretty = (n) => formatCurrency(n, 0);
  const fmtPct = (p) => {
    if (p == null || !isFinite(p)) return '—';
    const v = p * 100;
    const sign = v > 0 ? '+' : v < 0 ? '' : '';
    return `${sign}${v.toFixed(1)}%`;
  };
  const fmtDelta$ = (delta) => {
    const sign = delta > 0 ? '+' : '';
    return `${sign}${formatCurrency(delta, 0)}`;
  };
  const pctChange = (from, to) => {
    if (from === 0) return null;
    return (to - from) / from;
  };

  // Colors and styles
  const colors = {
    assets: 'linear-gradient(180deg, #3bc9db 0%, #228be6 100%)', // cyan to blue
    liabilities: 'linear-gradient(180deg, #ffa8a8 0%, #ff6b6b 100%)', // light red to red
    equity: 'linear-gradient(180deg, #b2f2bb 0%, #40c057 100%)', // light green to green
    // Subtle grid/plate behind columns (very light on dark)
    grid: 'rgba(255,255,255,0.04)',
    // Text hues tuned for dark theme
    text: '#e2e8f0', // slate-200
    subtle: '#94a3b8', // slate-400
  };

  const cardStyle = {
    border: '1px solid #1e293b', // slate-800
    borderRadius: 16,
    background: '#0f172a', // slate-900
    boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
    padding: 16,
  };

  const blockBase = {
    width: 240,
    borderRadius: 12,
    position: 'relative',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.4), 0 10px 20px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  };

  const labelStyle = {
    position: 'absolute',
    left: 10,
    right: 10,
    color: 'white',
    textShadow: '0 1px 2px rgba(0,0,0,0.4)',
    fontWeight: 600,
    letterSpacing: 0.2,
  };

  // Build delta display values
  const deltas = useMemo(() => {
    const make = (from, to) => ({
      delta: (to - from),
      pct: pctChange(from, to)
    });
    return {
      assets: {
        total: make(totals.baseTotalAssets, totals.totalAssets),
        cash: make(baselineData.assets.cash || 0, data.assets.cash || 0),
        invested: make(baselineData.assets.invested || 0, data.assets.invested || 0),
        physical: make(baselineData.assets.physical || 0, data.assets.physical || 0),
      },
      liabilities: {
        total: make(totals.baseTotalLiab, totals.totalLiab),
        mortgages: make(baselineData.liabilities.mortgages || 0, data.liabilities.mortgages || 0),
        auto_loans: make(baselineData.liabilities.auto_loans || 0, data.liabilities.auto_loans || 0),
        four01k_loans: make(baselineData.liabilities.four01k_loans || 0, data.liabilities.four01k_loans || 0),
        revolvers: make(baselineData.liabilities.revolvers || 0, data.liabilities.revolvers || 0),
      },
      equity: make(totals.baseEquity, totals.equity),
    };
  }, [data, baselineData, totals]);

  const renderChange = (d, variant = 'normal') => {
    if (!d) return null;
    const text = changeMode === '%' ? fmtPct(d.pct) : fmtDelta$(d.delta);
    return (
      <span style={{ fontSize: variant === 'sub' ? 10 : 12, fontStyle: 'italic', opacity: 0.85, marginLeft: 6 }}>
        ({text})
      </span>
    );
  };

  const periodButtons = (
    <div style={{display:'flex', gap:6, alignItems:'center', flexWrap:'wrap'}}>
      {[
        {k:'1m', label:'1M'},
        {k:'3m', label:'3M'},
        {k:'6m', label:'6M'},
        {k:'12m', label:'12M'},
        {k:'ytd', label:'YTD'},
        {k:'all', label:'All'},
      ].map(it => (
        <button key={it.k} className={`btn ${period===it.k ? 'btn-primary':'btn-light'}`} onClick={()=>setPeriod(it.k)}>{it.label}</button>
      ))}
      <div style={{marginLeft:8, display:'flex', alignItems:'center', gap:4}}>
        <span style={{fontSize:12, color: colors.subtle}}>Change:</span>
        <button className={`btn ${changeMode==='$' ? 'btn-secondary':'btn-light'}`} onClick={()=>setChangeMode('$')}>$</button>
        <button className={`btn ${changeMode==='%' ? 'btn-secondary':'btn-light'}`} onClick={()=>setChangeMode('%')}>%</button>
      </div>
    </div>
  );

  return (
    <div>
      <div className="card" style={{...cardStyle, marginTop:16}}>
        <div className="card-title" style={{color: colors.text, marginBottom: 12, display:'flex', alignItems:'center', justifyContent:'flex-start', gap:12, flexWrap:'wrap'}}>
          {/* Month snapshot picker moved to the left, replacing the old title */}
          <input
            id="snapshot-month"
            aria-label="Snapshot month"
            type="month"
            value={month}
            max={lastCompletedMonth}
            onChange={(e)=>{
              const v = e.target.value;
              const clamped = v > lastCompletedMonth ? lastCompletedMonth : v;
              setMonth(clamped);
            }}
            style={{padding:'6px 8px', background:'#0b1220', color: colors.text, border:'1px solid #334155', borderRadius:8}}
          />
          {/* Period preset buttons and $/% toggle to the right of the date picker */}
          {periodButtons}
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div style={{padding: 24}}>Loading...</div>
        ) : (
          <div style={{display:'flex', gap: 0, alignItems:'flex-end', justifyContent:'center', padding: 16, position:'relative'}}>
            <div style={{position:'absolute', inset:16, borderRadius:12, background: colors.grid, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)'}} />

            {/* Assets Column */}
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
              <div
                onClick={() => navigate('/balance_sheet/assets')}
                role="button"
                title="View Assets"
                aria-label="View Assets"
                style={{ ...blockBase, background: colors.assets, height: assetsHeight, minHeight: 20, display:'flex', flexDirection:'column', justifyContent:'flex-end', cursor:'pointer' }}
              >
                {/* Title and total at top inside block */}
                <div style={{...labelStyle, top: 8}}>
                  <div style={{fontSize:13, opacity:0.9}}>Assets</div>
                  <div style={{fontSize:18}}>
                    <span>{pretty(totals.totalAssets)}</span>
                    {renderChange(deltas.assets.total)}
                  </div>
                </div>
                {/* Breakdown inside the rectangle */}
                <div style={{padding:'10px 12px', color:'white'}}>
                  <BlockBreakdownItem label="Cash" value={pretty(data.assets.cash)} changeText={renderChange(deltas.assets.cash, 'sub')} dotColor="#4dabf7" />
                  <BlockBreakdownItem label="Invested" value={pretty(data.assets.invested)} changeText={renderChange(deltas.assets.invested, 'sub')} dotColor="#339af0" />
                  <BlockBreakdownItem label="Physical" value={pretty(data.assets.physical)} changeText={renderChange(deltas.assets.physical, 'sub')} dotColor="#228be6" />
                </div>
              </div>
            </div>

            {/* Liabilities + Equity Column */}
            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
              <div style={{ height: containerHeight, width: 240, position:'relative', display:'flex', flexDirection:'column', justifyContent:'flex-end' }}>
                {/* Equity block stacked above (rendered first so it sits above when bottom-aligned) */}
                <div
                  onClick={() => navigate('/balance_sheet/equity')}
                  role="button"
                  title="View Equity"
                  aria-label="View Equity"
                  style={{ ...blockBase, background: colors.equity, height: equityHeightRaw, minHeight: 0, cursor:'pointer' }}
                >
                  <div style={{...labelStyle, top: 8}}>
                    <div style={{fontSize:13, opacity:0.9}}>Equity</div>
                    <div style={{fontSize:18}}>
                      <span>{pretty(totals.equity)}</span>
                      {renderChange(deltas.equity)}
                    </div>
                  </div>
                </div>
                {/* Liabilities block on bottom (rendered last so it sits at the bottom of the stack) */}
                <div
                  onClick={() => navigate('/balance_sheet/financing')}
                  role="button"
                  title="View Liabilities"
                  aria-label="View Liabilities"
                  style={{ ...blockBase, background: colors.liabilities, height: liabHeightRaw, minHeight: 20, display:'flex', flexDirection:'column', justifyContent:'flex-end', cursor:'pointer' }}
                >
                  {/* Title and total at top inside block */}
                  <div style={{...labelStyle, top: 8}}>
                    <div style={{fontSize:13, opacity:0.9}}>Liabilities</div>
                    <div style={{fontSize:18}}>
                      <span>{pretty(totals.totalLiab)}</span>
                      {renderChange(deltas.liabilities.total)}
                    </div>
                  </div>
                  {/* Breakdown inside the rectangle */}
                  <div style={{padding:'10px 12px', color:'white'}}>
                    <BlockBreakdownItem label="Mortgages" value={pretty(data.liabilities.mortgages)} changeText={renderChange(deltas.liabilities.mortgages, 'sub')} dotColor="#ff6b6b" />
                    <BlockBreakdownItem label="Auto Loans" value={pretty(data.liabilities.auto_loans)} changeText={renderChange(deltas.liabilities.auto_loans, 'sub')} dotColor="#ff8787" />
                    {data.liabilities.four01k_loans > 0 && <BlockBreakdownItem label="401k Loans" value={pretty(data.liabilities.four01k_loans)} changeText={renderChange(deltas.liabilities.four01k_loans, 'sub')} dotColor="#ff922b" />}
                    <BlockBreakdownItem label="Revolving Debt" value={pretty(data.liabilities.revolvers)} changeText={renderChange(deltas.liabilities.revolvers, 'sub')} dotColor="#fa5252" />
                  </div>
                </div>
              </div>
              {/* External breakdown rows removed in favor of in-block details */}
              {totals.equity < 0 && (
                <div style={{fontSize:12, color:'#b91c1c'}}>Note: Equity is negative (liabilities exceed assets)</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, emphasize }) {
  const color = emphasize === 'positive' ? '#0f5132' : emphasize === 'negative' ? '#842029' : '#334155';
  const bg = emphasize === 'positive' ? '#d1e7dd' : emphasize === 'negative' ? '#f8d7da' : '#f1f5f9';
  return (
    <div style={{background:bg, padding:'10px 12px', borderRadius:10}}>
      <div style={{fontSize:12, color:'#64748b', fontWeight:600}}>{label}</div>
      <div style={{fontSize:18, color, fontWeight:700}}>{value}</div>
    </div>
  );
}

function BreakdownRow({ label, value, color }) {
  return (
    <div style={{display:'flex', gap:8, alignItems:'center'}}>
      <span style={{display:'inline-block', width:10, height:10, background:color, borderRadius:2}} />
      <span style={{fontSize:12, color:'#64748b', width:84}}>{label}</span>
      <span style={{fontSize:12, color:'#1f2937', fontWeight:600}}>{value}</span>
    </div>
  );
}

// Compact item for inside-rectangle listing
function BlockBreakdownItem({ label, value, changeText, dotColor }) {
  return (
    <div style={{display:'flex', alignItems:'center', gap:8, lineHeight:1.2, marginTop:4}}>
      <span style={{width:8, height:8, background:dotColor, borderRadius:2, display:'inline-block', opacity:0.9}} />
      <span style={{fontSize:12, opacity:0.9}}>{label}</span>
      <span style={{marginLeft:'auto', fontSize:12, fontWeight:700}}>{value}{changeText || ''}</span>
    </div>
  );
}
